import React, { useState, useEffect } from 'react';
import apiClient from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, RotateCcw } from 'lucide-react';

const STATUS_CLS = {
  PROCESSED: 'text-green-600 bg-green-50',
  PENDING: 'text-amber-600 bg-amber-50',
  INITIATED: 'text-amber-600 bg-amber-50',
  FAILED: 'text-red-600 bg-red-50',
};

export default function RefundDialog({ order, open, onClose, onRefunded }) {
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState('full');       // full | partial
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!order) return;
    apiClient.get(`/refunds/order/${order.id}`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  };

  useEffect(() => {
    if (open && order) { setMode('full'); setAmount(''); setReason(''); load(); }
    // eslint-disable-next-line
  }, [open, order]);

  const refundable = (summary?.refundable_paise || 0) / 100;

  const submit = async () => {
    if (mode === 'partial') {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
      if (amt > refundable) { toast.error(`Max refundable is ₹${refundable.toFixed(2)}`); return; }
    }
    setBusy(true);
    try {
      const body = { order_id: order.id, reason: reason || null };
      if (mode === 'partial') body.amount = parseFloat(amount);
      const r = await apiClient.post('/refunds', body);
      toast.success(`Refund ${r.data.refund.status.toLowerCase()} · ₹${r.data.refund.amount}`);
      setAmount(''); setReason(''); load();
      onRefunded && onRefunded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refund failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund · Order #{order?.order_number}</DialogTitle>
          <DialogDescription className="sr-only">Issue a full or partial refund for this order.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="bg-slate-50 rounded-lg p-2"><p className="text-slate-400">Captured</p><p className="font-bold" data-testid="refund-captured">₹{((summary?.captured_paise || 0) / 100).toFixed(2)}</p></div>
          <div className="bg-slate-50 rounded-lg p-2"><p className="text-slate-400">Refunded</p><p className="font-bold" data-testid="refund-refunded">₹{((summary?.refunded_paise || 0) / 100).toFixed(2)}</p></div>
          <div className="bg-slate-50 rounded-lg p-2"><p className="text-slate-400">Refundable</p><p className="font-bold text-sspice" data-testid="refund-refundable">₹{refundable.toFixed(2)}</p></div>
        </div>

        {refundable > 0 ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <button onClick={() => setMode('full')} data-testid="refund-mode-full"
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${mode === 'full' ? 'bg-sspice text-white border-sspice' : 'border-slate-200 text-slate-600'}`}>Full (₹{refundable.toFixed(2)})</button>
              <button onClick={() => setMode('partial')} data-testid="refund-mode-partial"
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${mode === 'partial' ? 'bg-sspice text-white border-sspice' : 'border-slate-200 text-slate-600'}`}>Partial</button>
            </div>
            {mode === 'partial' && (
              <Input type="number" placeholder="Amount (₹)" value={amount}
                onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount-input" />
            )}
            <Input placeholder="Reason (optional)" value={reason}
              onChange={(e) => setReason(e.target.value)} data-testid="refund-reason-input" />
            <Button className="w-full bg-sspice hover:bg-sspice-hover" disabled={busy} onClick={submit} data-testid="refund-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-2" /> Issue Refund</>}
            </Button>
          </div>
        ) : (
          <p className="text-center text-sm text-slate-400 py-2" data-testid="refund-none-left">Nothing left to refund.</p>
        )}

        {summary?.refunds?.length > 0 && (
          <div className="border-t border-slate-100 pt-3 space-y-2 max-h-40 overflow-auto" data-testid="refund-history">
            {summary.refunds.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs" data-testid={`refund-row-${r.id}`}>
                <span className="text-slate-600">{r.is_partial ? 'Partial' : 'Full'} · {r.provider}{r.reason ? ` · ${r.reason}` : ''}</span>
                <span className="flex items-center gap-2">
                  <span className="font-semibold">₹{r.amount}</span>
                  <span className={`px-1.5 py-0.5 rounded ${STATUS_CLS[r.status] || ''}`}>{r.status}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
